import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import * as yup from 'yup'
import { MEETING_TICKET_KEY, SERVICE_SUSPEND_KEY } from '../../constants'

export const createTicketSchema = (t: TFunction<'translation', undefined>) =>
    yup.object().shape({
        ticket_type_id: yup
            .number()
            .required(t('blank-errors'))
            .moreThan(0, t('Ticket type is required and must be a number')),

        ticket_type_key: yup.string(),

        // Required when ticket_type_key === 'meeting'
        ticket_meeting_purpose: yup.string().when('ticket_type_key', {
            is: MEETING_TICKET_KEY,
            then: (schema) => schema.required(t('meeting-purpose-required')),
            otherwise: (schema) => schema.notRequired(),
        }),

        // Required when ticket_type_key === 'meeting'
        ticket_meeting_via: yup.string().when('ticket_type_key', {
            is: MEETING_TICKET_KEY,
            then: (schema) => schema.required(t('meeting-via-required')),
            otherwise: (schema) => schema.notRequired(),
        }),

        // Ngày khách hàng request
        ticket_requested_at: yup.mixed().nullable(),

        // TG dự tính bắt đầu xử lý
        // - Không được nhỏ hơn ticket_requested_at
        // - Không được lớn hơn ticket_estimated_completion_date
        ticket_time_repply: yup
            .mixed()
            .nullable()
            .test(
                'not-before-requested',
                t('time-repply-not-before-requested'),
                function (value) {
                    if (!value) return true // Allow null/undefined
                    const { ticket_requested_at } = this.parent
                    if (!ticket_requested_at) return true
                    return dayjs(value as string).isSameOrAfter(
                        dayjs(ticket_requested_at as string),
                        'minute'
                    )
                }
            )
            .test(
                'not-after-completion',
                t('time-repply-not-after-completion'),
                function (value) {
                    if (!value) return true // Allow null/undefined
                    const { ticket_estimated_completion_date } = this.parent
                    if (!ticket_estimated_completion_date) return true
                    return dayjs(value as string).isSameOrBefore(
                        dayjs(ticket_estimated_completion_date as string),
                        'minute'
                    )
                }
            ),

        // TG hoàn thành dự kiến
        // Not required when ticket_type_key === 'meeting'
        ticket_estimated_completion_date: yup
            .mixed()
            .nullable()
            .when('ticket_type_key', {
                is: MEETING_TICKET_KEY,
                then: (schema) => schema.notRequired(),
                otherwise: (schema) => schema.notRequired(),
            })
            .test(
                'not-before-repply',
                t('completion-not-before-repply'),
                function (value) {
                    if (!value) return true
                    const { ticket_time_repply } = this.parent
                    if (!ticket_time_repply) return true
                    return dayjs(value as string).isSameOrAfter(
                        dayjs(ticket_time_repply as string),
                        'minute'
                    )
                }
            ),

        // Required when ticket_type_key !== 'internal_problem'
        customer_id: yup.number().when('ticket_type_key', {
            is: 'internal_problem',
            then: (schema) => schema.notRequired(),
            otherwise: (schema) =>
                schema
                    .required(t('customer-required'))
                    .moreThan(0, t('customer-required')),
        }),

        // Required when ticket_type_key !== 'internal_problem'
        // Check if service_id has value OR ticket_product_name is not empty
        service_id: yup.mixed(), // can be string or number

        // Required when ticket_type_key === 'service_suspend'
        service_suspend_until: yup.mixed().when('ticket_type_key', {
            is: SERVICE_SUSPEND_KEY,
            then: (schema) =>
                schema.required(t('service-suspend-until-required')),
            otherwise: (schema) => schema.notRequired(),
        }),

        ticket_product_name: yup
            .string()
            .when(['ticket_type_key', 'service_id'], {
                is: (ticketTypeKey: string, serviceId: string | number) => {
                    if (ticketTypeKey === 'internal_problem') return false
                    // Check if service_id is empty (handles both string and number)
                    const hasServiceId =
                        serviceId &&
                        ((typeof serviceId === 'number' && serviceId > 0) ||
                            (typeof serviceId === 'string' &&
                                serviceId.trim() !== ''))
                    return !hasServiceId
                },
                then: (schema) =>
                    schema.required(t('service-or-product-required')),
                otherwise: (schema) => schema.notRequired(),
            }),
    })
